const SCROLL_LOCK_CLASS = 'scroll-lock';
const SCROLLBAR_GUTTER_CLASS = '[scrollbar-gutter:stable]';

interface Holds {
  lock: number;
  gutter: number;
}

/** Outstanding holds per selector. See `addScrollLock` for why they are counted. */
const holdsBySelector = new Map<string, Holds>();

/**
 * Locks page scroll on the element matching `selector` and returns a function that releases *this*
 * hold. Calling the returned function more than once is a no-op.
 *
 * Holds are ref-counted because surfaces nest: a modal opened from inside another modal takes its
 * own lock, and releasing the inner one must not unlock the page behind the outer one that is still
 * open. The classes come off only once the last holder has released.
 *
 * @param keepScrollBarGap - reserve the scrollbar's width to prevent layout shift.
 */
export function addScrollLock(selector: string, keepScrollBarGap = false): () => void {
  const holds = holdsBySelector.get(selector) ?? {lock: 0, gutter: 0};
  holds.lock += 1;
  if (keepScrollBarGap) {
    holds.gutter += 1;
  }
  holdsBySelector.set(selector, holds);

  const el = global.document.querySelector(selector);
  el?.classList.add(SCROLL_LOCK_CLASS);
  if (keepScrollBarGap) {
    el?.classList.add(SCROLLBAR_GUTTER_CLASS);
  }

  let isReleased = false;
  return () => {
    if (isReleased) {
      return;
    }
    isReleased = true;

    holds.lock -= 1;
    if (keepScrollBarGap) {
      holds.gutter -= 1;
    }

    const target = global.document.querySelector(selector);
    if (holds.gutter <= 0) {
      target?.classList.remove(SCROLLBAR_GUTTER_CLASS);
    }
    if (holds.lock <= 0) {
      target?.classList.remove(SCROLL_LOCK_CLASS);
      holdsBySelector.delete(selector);
    }
  };
}
